/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { getUrlSync, isOboRequest } from '@msdyn365-commerce/core';
import {
    BusinessPartnerOperationDeliveryType,
    BusinessPartnerUsersDataActions,
    InvoicePaidStatus,
    InvoiceSearchCriteria,
    IQueryResultSettings,
    OrderInvoice
} from '@msdyn365-commerce/retail-proxy';
import { getCurrentAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/BusinessPartnersDataActions.g';
import {
    ArrayExtensions,
    getCustomer,
    GetCustomerInput,
    getInvoices,
    GetInvoicesInput,
    IQueryResultSettingsModuleConfig,
    QueryResultSettingsProxy,
    StringExtensions
} from '@msdyn365-commerce-modules/retail-actions';
import { Button, Heading, IModuleProps } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';
import { reaction } from 'mobx';
import { observer } from 'mobx-react';

import { InvoiceErrorComponent } from './components/invoice-error-component';
import { InvoicesFilterComponent, InvoicesFilterState } from './components/invoices-filter-component';
import { InvoicesTableComponent } from './components/invoices-table/invoices-table-component';
import { IMakePaymentResources, MakePaymentComponent } from './components/make-payment-component';
import { RequestInvoiceButton } from './components/request-invoice-button';
import { RequestInvoiceModal } from './components/request-invoice-modal';
import { InvoiceModel } from './helpers/invoice-data';
import { IAddInvoicesToCartFailureResult } from './helpers/pay-invoices';
import { IInvoicesListData } from './invoices-list.data';
import { IInvoicesListProps } from './invoices-list.props.autogenerated';

export type InvoicesListStatus = 'LOADING' | 'SUCCESS' | 'FAILED' | 'EMPTY';
export type InvoiceErrorHost = 'ADDINVOICETOCART';

export interface IInvoicesListViewProps extends IInvoicesListProps<IInvoicesListData> {
    moduleProps: IModuleProps;

    header: JSX.Element;
    filter: JSX.Element;
    content?: JSX.Element;
    invoiceRequestModal?: JSX.Element;
    pagination?: JSX.Element;
}

export interface IInvoiceErrorState {
    errorHost?: InvoiceErrorHost;
    errorReason?: IAddInvoicesToCartFailureResult;
    errorMessage: string;
    otherError?: string;
    errorCode?: string;
}

export interface IInvoicesListState {
    invoices: InvoiceModel[];
    invoicesFilterState: InvoicesFilterState;
    errorState: IInvoiceErrorState;
    isInvoiceRequestModalOpen: boolean;
    isLoading: boolean;
    isPageLoaded: boolean;
    payInvoiceErrorModalOpen: boolean;
    selectedButtonElement?: HTMLButtonElement | null;
}

/**
 * InvoicesList component.
 * @extends {React.Component<IInvoicesListProps<IInvoicesListData>, IInvoicesListState>}
 */
@observer
class InvoicesList extends React.Component<IInvoicesListProps<IInvoicesListData>, IInvoicesListState> {
    private readonly _className: string = 'msc-invoices-list';

    private readonly payInvoiceRequestReference: React.RefObject<HTMLButtonElement>;

    private readonly makePaymentReference: React.RefObject<HTMLButtonElement>;

    private buttonChildReference: React.RefObject<HTMLButtonElement>[] = [];

    private buttonChildPayButtonReference: React.RefObject<HTMLButtonElement>[] = [];

    private readonly _queryResultSettings: IQueryResultSettings;

    private invoiceIds: string[] = [];

    constructor(props: IInvoicesListProps<IInvoicesListData>) {
        super(props);

        this._queryResultSettings = QueryResultSettingsProxy.fromModuleData(
            this.props.context.request,
            this.props.config as IQueryResultSettingsModuleConfig
        ).QueryResultSettings;
        this.payInvoiceRequestReference = React.createRef<HTMLButtonElement>();
        this.makePaymentReference = React.createRef<HTMLButtonElement>();
        this._updateInvoicesState = this._updateInvoicesState.bind(this);
        this._onAddToCartFailed = this._onAddToCartFailed.bind(this);
        this._onToggleInvoiceRequestModal = this._onToggleInvoiceRequestModal.bind(this);
        this._handleFilterStateChange = this._handleFilterStateChange.bind(this);
        this._loadMoreInvoices = this._loadMoreInvoices.bind(this);
        this._requestInvoice = this._requestInvoice.bind(this);
        this._requestSpecificInvoice = this._requestSpecificInvoice.bind(this);
        this._setPayInvoiceErrorModalClose = this._setPayInvoiceErrorModalClose.bind(this);
        this.state = {
            invoices: [],
            invoicesFilterState: InvoicesFilterState.ShowAll,
            isLoading: true,
            isPageLoaded: false,
            errorState: { errorMessage: '' },
            isInvoiceRequestModalOpen: false,
            payInvoiceErrorModalOpen: false,
            selectedButtonElement: null
        };
    }

    public shouldComponentUpdate(nextProps: IInvoicesListProps<IInvoicesListData>, nextState: IInvoicesListState): boolean {
        if (this.state === nextState && this.props.data === nextProps.data) {
            return false;
        }
        return true;
    }

    public render(): JSX.Element | null {
        const {
            config: { className = '', heading },
            context: {
                request: { channel },
                cultureFormatter: {
                    formatCurrency,

                    // @ts-expect-error
                    currencyCode
                }
            },
            data: { customerBalance },
            resources: { invoicesListTitle, invoicesListDescTitle, invoicesListDescCount }
        } = this.props;

        const totalAmount = customerBalance.result?.OpenInvoiceBalance || 0;
        const amountText = `${formatCurrency(totalAmount.toFixed(2), currencyCode)} ${channel?.Currency || 'USD'}`;

        const totalCount = customerBalance.result?.OpenInvoiceCount || 0;
        const countText = invoicesListDescCount.replace('{0}', totalCount.toString());
        const invoicesListTitleHeading = heading?.text ?? invoicesListTitle;

        const headerClassName = `${this._className}__header`;

        const header = (
            <div className={headerClassName}>
                <Heading className={`${headerClassName}-heading`} text={invoicesListTitleHeading} />
                <div className={`${headerClassName}-description`}>
                    <div className={`${headerClassName}-description-title`}>{invoicesListDescTitle}</div>
                    <div className={`${headerClassName}-description-amount`}>{amountText}</div>
                    <div className={`${headerClassName}-description-count`}>{countText}</div>
                </div>
            </div>
        );

        const viewProps: IInvoicesListViewProps = {
            ...(this.props as IInvoicesListProps<IInvoicesListData>),
            moduleProps: {
                moduleProps: this.props,
                className: classnames(this._className, className)
            },
            header,
            invoiceRequestModal: this._renderInvoiceRequestModal(),
            filter: this._renderInvoicesFilter(this._className),
            pagination: this._renderPagination()
        };

        if (!this.state.isPageLoaded) {
            return this.props.renderView(viewProps);
        }

        if (ArrayExtensions.hasElements(this.state.invoices)) {
            viewProps.content = this._renderListContainer();
        } else {
            viewProps.content = this._renderEmptyInvoiceLinesPageContent();
        }

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    public async componentDidMount(): Promise<void> {
        await this.props.data.customer;
        const invoicesData = await this.props.data.invoices;
        this._initializeWithInvoices(invoicesData);

        reaction(
            () => this.state.isInvoiceRequestModalOpen,
            async () => {
                if (this.invoiceIds && !this.state.isInvoiceRequestModalOpen) {
                    const context = { callerContext: this.props.context.actionContext };
                    const b2bUserId: string = this.props.data.b2bUserInformation?.result?.B2BUserId || '';
                    for (const invoiceId of this.invoiceIds) {
                        await BusinessPartnerUsersDataActions.requestInvoiceCopyAsync(
                            context,
                            b2bUserId,
                            invoiceId,
                            BusinessPartnerOperationDeliveryType.Email
                        );
                    }
                    this.invoiceIds = [];
                }
            }
        );
    }

    private _initializeWithInvoices(invoicesData: OrderInvoice[]): void {
        const invoices = invoicesData.map((invoice, index) => {
            this.buttonChildReference[Number(index)] = React.createRef<HTMLButtonElement>();
            this.buttonChildPayButtonReference[Number(index)] = React.createRef<HTMLButtonElement>();
            return new InvoiceModel(invoice);
        });
        this.setState({
            invoices,
            isLoading: false,
            isPageLoaded: true
        });
    }

    private _addInvoices(invoicesData: OrderInvoice[]): void {
        const newInvoices = invoicesData.map(invoice => new InvoiceModel(invoice));
        const invoices = this.state.invoices.concat(newInvoices);
        this.setState({
            invoices,
            isLoading: false
        });
    }

    private _renderEmptyInvoiceLinesPageContent(): JSX.Element {
        const containerClass = `${this._className}__empty-lines__container`;
        const actionsContainerClass = `${containerClass}__actions`;
        return (
            <div className={containerClass}>
                <div className={`${containerClass}__title`}>{this.props.resources.invoiceEmptyInvoiceLinesTitle}</div>
                <div className={`${containerClass}__description`}>{this.props.resources.invoiceEmptyInvoiceLinesDescription}</div>
                <div className={actionsContainerClass}>
                    <Button
                        className={`${actionsContainerClass}__continue-shopping`}
                        aria-label={this.props.resources.invoiceEmptyInvoiceLinesContinueShoppingButtonAriaLabel}
                        href={getUrlSync('home', this.props.context.actionContext)}
                    >
                        {this.props.resources.invoiceEmptyInvoiceLinesContinueShoppingButtonText}
                    </Button>
                </div>
            </div>
        );
    }

    private _renderListContainer(): JSX.Element {
        const containerClass = `${this._className}__container`;
        const containerHeaderClass = `${containerClass}__header`;
        const containerContentClass = `${containerClass}__content`;
        const isOnBehalfOfRequest = isOboRequest(this.props.context.request);

        return (
            <div className={containerClass}>
                <div className={containerHeaderClass}>
                    {this._renderMakePayment(containerHeaderClass, isOnBehalfOfRequest)}
                    {this._renderInvoiceRequestButton()}
                </div>
                <div className={containerContentClass}>{this._renderInvoiceErrors()}</div>
                <div className={containerContentClass}>{this._renderInvoicesList(containerContentClass)}</div>
            </div>
        );
    }

    private _updateInvoicesState(): void {
        this.setState({
            invoices: this.state.invoices
        });
    }

    private async _getInvoicesSearchCriteria(state: InvoicesFilterState): Promise<InvoiceSearchCriteria> {
        const searchCriteria: InvoiceSearchCriteria = {};

        const context = this.props.context.actionContext;

        const businessPartner = await getCurrentAsync({ callerContext: context });
        searchCriteria.BusinessPartnerId = businessPartner.BusinessPartnerId;

        const customer = await getCustomer(new GetCustomerInput(context.requestContext.apiSettings), context);
        searchCriteria.CustomerId = customer.AccountNumber;

        switch (state) {
            case InvoicesFilterState.ShowAll:
                searchCriteria.InvoicePaidStatusValues = [InvoicePaidStatus.None];
                break;
            case InvoicesFilterState.Paid:
                searchCriteria.InvoicePaidStatusValues = [InvoicePaidStatus.Paid];
                break;
            case InvoicesFilterState.PartiallyPaid:
                searchCriteria.InvoicePaidStatusValues = [InvoicePaidStatus.PartiallyPaid];
                break;
            case InvoicesFilterState.Unpaid:
                searchCriteria.InvoicePaidStatusValues = [InvoicePaidStatus.Unpaid];
                break;
            default:
                throw new Error('Failed to process unknown invoices filter state.');
        }

        return searchCriteria;
    }

    private async _handleFilterStateChange(state: InvoicesFilterState): Promise<void> {
        this.setState({
            invoicesFilterState: state,
            isLoading: true
        });

        const searchCriteria = await this._getInvoicesSearchCriteria(state);

        if (this._queryResultSettings.Paging) {
            this._queryResultSettings.Paging.Skip = 0;
        }

        const invoices = await getInvoices(
            new GetInvoicesInput(searchCriteria, this._queryResultSettings),
            this.props.context.actionContext
        );
        this._initializeWithInvoices(invoices);
    }

    private _renderInvoicesFilter(className: string): JSX.Element {
        const filterClassName = `${className}__filter`;
        return (
            <InvoicesFilterComponent
                id={filterClassName}
                typeName={this.props.typeName}
                key={filterClassName}
                className={filterClassName}
                context={this.props.context}
                resources={this.props.resources}
                data={{}}
                currentState={InvoicesFilterState.ShowAll}
                onFilterStateChanged={this._handleFilterStateChange}
                disabled={this.state.isLoading}
            />
        );
    }

    private _renderInvoicesList(className: string): JSX.Element {
        const tableClassName = `${className}__table`;
        return (
            <InvoicesTableComponent
                invoiceButtonReferences={this.buttonChildReference}
                payInvoiceButtonReferences={this.buttonChildPayButtonReference}
                id={tableClassName}
                typeName={this.props.typeName}
                key={tableClassName}
                className={tableClassName}
                data={{ invoices: this.state.invoices, customer: this.props.data.customer.result! }}
                context={this.props.context}
                resources={this.props.resources}
                onChange={this._updateInvoicesState}
                onError={this._onAddToCartFailed}
                onRequestInvoice={this._requestSpecificInvoice}
            />
        );
    }

    private _onAddToCartFailed(newErrorState: IInvoiceErrorState, reference?: React.RefObject<HTMLButtonElement>): void {
        const errorState = { ...newErrorState };
        errorState.errorHost = 'ADDINVOICETOCART';
        errorState.errorMessage =
            newErrorState.errorReason?.failureReason === 'EMPTYINPUT'
                ? this.props.resources.invoiceErrorInvoiceIdNotExist
                : this.props.resources.invoiceErrorFailedAddInvoiceToCart;

        const shouldShowAdditionalInfoInPopup =
            errorState.errorCode === 'Microsoft_Dynamics_Commerce_Runtime_InvoiceLineNotAllowedWithItemLines';
        this.setState({ errorState, payInvoiceErrorModalOpen: shouldShowAdditionalInfoInPopup, selectedButtonElement: reference?.current });
    }

    private _renderMakePayment(className: string, disabled: boolean): JSX.Element {
        const id = `${className}__make-payment`;
        const makePaymentResources: IMakePaymentResources = {
            makePaymentButtonText: this.props.resources.makePaymentButtonText,
            makePaymentButtonAriaLabel: this.props.resources.makePaymentButtonAriaLabel
        };

        const selectedInvoices = this._getSelectedInvoices();

        return (
            <MakePaymentComponent
                id={id}
                typeName={this.props.typeName}
                key={id}
                data={{ invoices: selectedInvoices }}
                context={this.props.context}
                telemetry={this.props.telemetry}
                moduleId={this.props.id}
                moduleTypeName={this.props.typeName}
                navigationUrl={getUrlSync('cart', this.props.context.actionContext)}
                resources={makePaymentResources}
                onError={this._onAddToCartFailed}
                buttonReferences={this.makePaymentReference}
                disabled={disabled}
            />
        );
    }

    private _setPayInvoiceErrorModalClose(): void {
        this.setState({
            payInvoiceErrorModalOpen: false
        });
        this.state.selectedButtonElement?.focus();
    }

    private _renderInvoiceErrors(): JSX.Element {
        const {
            resources: {
                payInvoiceErrorDialogHeader,
                payInvoiceErrorDialogBodyErrorTitle,
                payInvoiceErrorDialogBodyErrorDescription,
                payInvoiceErrorDialogFooterGoToCartButtonText,
                payInvoiceErrorDialogFooterCancelButtonText
            }
        } = this.props;

        const cartUrl = getUrlSync('cart', this.props.context.actionContext) || '';

        const errorClassName = `${this._className}__errors`;

        return (
            <InvoiceErrorComponent
                id={errorClassName}
                typeName={this.props.typeName}
                data={{}}
                messageHeader={this.props.resources.invoiceErrorMessageHeader}
                context={this.props.context}
                errorMessage={this.state.errorState.errorMessage}
                otherError={this.state.errorState.otherError}
                showError={this.state.errorState.errorHost === 'ADDINVOICETOCART'}
                payInvoiceErrorModalOpen={this.state.payInvoiceErrorModalOpen}
                errorDialogHeader={payInvoiceErrorDialogHeader}
                errorDialogBodyErrorTitle={payInvoiceErrorDialogBodyErrorTitle}
                errorDialogBodyErrorDescription={payInvoiceErrorDialogBodyErrorDescription}
                errorDialogFooterGoToCartButtonText={payInvoiceErrorDialogFooterGoToCartButtonText}
                errorDialogFooterCancelButtonText={payInvoiceErrorDialogFooterCancelButtonText}
                cartUrl={cartUrl}
                setPayInvoiceErrorModalClose={this._setPayInvoiceErrorModalClose}
            />
        );
    }

    private _renderPagination(): JSX.Element | undefined {
        const { data, resources } = this.props;
        const invoices = data?.invoices?.result;
        if (!invoices || invoices.length < this._queryResultSettings.Paging!.Top!) {
            return undefined;
        }

        return (
            <Button
                className={classnames(`${this._className}__load-more`, this.state.isLoading ? 'is-busy' : '')}
                disabled={this.state.isLoading}
                onClick={this._loadMoreInvoices}
            >
                {resources.invoicesLoadMore}
            </Button>
        );
    }

    private async _loadMoreInvoices(): Promise<void> {
        this.setState({ isLoading: true });
        const searchCriteria = await this._getInvoicesSearchCriteria(this.state.invoicesFilterState);
        if (this._queryResultSettings.Paging) {
            this._queryResultSettings.Paging.Skip! += this._queryResultSettings.Paging.Top!;
        }
        const newInvoices = await getInvoices(
            new GetInvoicesInput(searchCriteria, this._queryResultSettings),
            this.props.context.actionContext
        );
        this._addInvoices(newInvoices);
    }

    private _renderInvoiceRequestModal(): JSX.Element {
        const customer = this.props.data.customer.result;
        const {
            invoiceRequestModalCancelLabel,
            invoiceRequestModalSubmitLabel,
            invoiceRequestModalHeaderLabel,
            invoiceRequestModalSendToEmailLabel,
            invoiceRequestModalSearch,
            invoiceRequestModalInvoiceSearchDisplay,
            invoiceRequestModalSingleInvoiceSearchDisplay,
            invoiceRequestModalSearchButton
        } = this.props.resources;

        return (
            <RequestInvoiceModal
                className={this._className}
                email={customer?.Email || ''}
                isModalOpen={this.state.isInvoiceRequestModalOpen}
                toggle={this._onToggleInvoiceRequestModal}
                cancelLabel={invoiceRequestModalCancelLabel}
                modalHeaderLabel={invoiceRequestModalHeaderLabel}
                sendToEmailLabel={invoiceRequestModalSendToEmailLabel}
                submitRequestLabel={invoiceRequestModalSubmitLabel}
                onSubmitRequest={this._requestInvoice}
                invoices={this.state.invoices}
                searchPlaceholderLabel={invoiceRequestModalSearch}
                invoicesFoundString={invoiceRequestModalInvoiceSearchDisplay}
                singleInvoiceFoundString={invoiceRequestModalSingleInvoiceSearchDisplay}
                searchButtonAltText={invoiceRequestModalSearchButton}
                resources={this.props.resources}
            />
        );
    }

    private _onToggleInvoiceRequestModal(): void {
        this.setState({
            isInvoiceRequestModalOpen: !this.state.isInvoiceRequestModalOpen
        });
        if (this.state.isInvoiceRequestModalOpen) {
            this.state.selectedButtonElement?.focus();
        }
    }

    /**
     * Request specific invoice function.
     */
    private readonly _onToggleInvoiceRequestParentModal = () => {
        this.setState(
            {
                selectedButtonElement: this.payInvoiceRequestReference.current
            },
            () => {
                this._onToggleInvoiceRequestModal();
            }
        );
    };

    private _renderInvoiceRequestButton(): JSX.Element | null {
        const { invoiceRequestButtonLabel } = this.props.resources;
        const customer = this.props.data.customer.result;

        if (StringExtensions.isNullOrWhitespace(customer?.Email)) {
            return null;
        }

        return (
            <RequestInvoiceButton
                innerReference={this.payInvoiceRequestReference}
                requestInvoiceButtonLabel={invoiceRequestButtonLabel}
                onClicked={this._onToggleInvoiceRequestParentModal}
                className={this._className}
            />
        );
    }

    private _requestInvoice(invoiceId: string): void {
        this.invoiceIds.push(invoiceId);
        this._onToggleInvoiceRequestModal();
    }

    private _getSelectedInvoices(): InvoiceModel[] {
        let selectedInvoices = this.state.invoices.filter(invoice => invoice.isSelected);
        if (!ArrayExtensions.hasElements(selectedInvoices)) {
            selectedInvoices = this.state.invoices;
        }

        return selectedInvoices;
    }

    private _requestSpecificInvoice(
        specificInvoice: InvoiceModel,
        requestInvoiceButtonReference: React.RefObject<HTMLButtonElement>
    ): void {
        this.state.invoices.forEach((invoice: InvoiceModel) => {
            if (invoice === specificInvoice) {
                invoice.isSelected = true;
            } else {
                invoice.isSelected = false;
            }
        });
        this.setState({
            selectedButtonElement: requestInvoiceButtonReference.current
        });
        this._onToggleInvoiceRequestModal();
    }
}

export default InvoicesList;
